import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { 
    CheckCircle2,
    XCircle,
    Loader2,
    PauseCircle,
    StopCircle,
    Clock,
    AlertTriangle,
    Ban,
    Search,
    ExternalLink,
    ArrowUp,
    ArrowDown,
    ArrowUpDown
} from "lucide-react";
import { format } from 'date-fns';
import type { RunResult, RunSortBy, SortOrder } from '@/api/runs/runs.types';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { RunSearchTableSkeleton } from "./RunSearchTableSkeleton";
import { FadeId } from "@/components/ui/fade-id";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface RunSearchTableProps {
    runs: RunResult[];
    isLoading: boolean;
    sortBy: RunSortBy;
    sortOrder: SortOrder;
    onSortChange: (sortBy: RunSortBy, sortOrder: SortOrder) => void;
}

// Status badge configuration
const statusConfig: Record<string, { 
    icon: React.ElementType; 
    className: string;
    label: string;
}> = {
    SUCCEEDED: {
        icon: CheckCircle2,
        className: "bg-green-100 text-green-800 border-green-200/80 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20",
        label: "Succeeded"
    },
    FAILED: {
        icon: XCircle,
        className: "bg-red-100 text-red-800 border-red-200/80 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20",
        label: "Failed"
    },
    RUNNING: {
        icon: Loader2,
        className: "bg-blue-100 text-blue-800 border-blue-200/80 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20",
        label: "Running"
    },
    PAUSED: {
        icon: PauseCircle,
        className: "bg-yellow-100 text-yellow-800 border-yellow-200/80 dark:bg-yellow-500/10 dark:text-yellow-400 dark:border-yellow-500/20",
        label: "Paused"
    },
    STOPPED: {
        icon: StopCircle,
        className: "bg-gray-100 text-gray-800 border-gray-200/80 dark:bg-gray-500/10 dark:text-gray-400 dark:border-gray-500/20",
        label: "Stopped"
    },
    TIMEOUT: {
        icon: Clock,
        className: "bg-orange-100 text-orange-800 border-orange-200/80 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20",
        label: "Timeout"
    },
    INTERNAL_ERROR: {
        icon: AlertTriangle,
        className: "bg-red-100 text-red-800 border-red-200/80 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20",
        label: "Internal Error"
    },
    QUOTA_EXCEEDED: {
        icon: Ban,
        className: "bg-purple-100 text-purple-800 border-purple-200/80 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20",
        label: "Quota Exceeded"
    }
};

export const RunSearchTable = ({ 
    runs, 
    isLoading,
    sortBy,
    sortOrder,
    onSortChange
}: RunSearchTableProps) => {

    const handleSort = (field: RunSortBy) => {
        if (sortBy === field) {
            // Toggle order if same field
            onSortChange(field, sortOrder === 'desc' ? 'asc' : 'desc');
        } else {
            // New field, default to desc
            onSortChange(field, 'desc');
        }
    };

    const getSortIcon = (field: RunSortBy) => {
        if (sortBy !== field) {
            return <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />;
        }
        return sortOrder === 'desc' 
            ? <ArrowDown className="h-3.5 w-3.5 text-primary" />
            : <ArrowUp className="h-3.5 w-3.5 text-primary" />;
    };

    const SortableHeader = ({ field, children }: { field: RunSortBy; children: React.ReactNode }) => (
        <button
            onClick={() => handleSort(field)}
            className={cn(
                "inline-flex items-center gap-1.5 hover:text-foreground transition-colors whitespace-nowrap",
                sortBy === field ? "text-foreground font-medium" : "text-muted-foreground"
            )}
        >
            {children}
            {getSortIcon(field)}
        </button>
    );

    const getStatusBadge = (status: string, hasFailedSteps: boolean) => {
        const config = statusConfig[status] || statusConfig.FAILED;
        const Icon = config.icon;
        const isRunning = status === 'RUNNING';
        
        // If status is SUCCEEDED but has failed steps, show a warning indicator
        const showWarning = status === 'SUCCEEDED' && hasFailedSteps;
        
        return (
            <div className="flex items-center gap-1.5">
                <div 
                    className={`
                        inline-flex items-center gap-1.5
                        text-xs font-medium px-2.5 py-1
                        rounded-full whitespace-nowrap border
                        ${config.className}
                    `}
                >
                    <Icon className={`h-3 w-3 ${isRunning ? 'animate-spin' : ''}`} />
                    <span>{config.label}</span>
                </div>
                {showWarning && (
                    <TooltipProvider delayDuration={200}>
                        <Tooltip>
                            <TooltipTrigger>
                                <div className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200/80 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20">
                                    <AlertTriangle className="h-3 w-3" />
                                </div>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>This run completed but has failed steps inside</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
            </div>
        );
    };

    const getTimeZoneString = () => {
        const offset = new Date().getTimezoneOffset();
        const hours = Math.abs(Math.floor(offset / 60));
        const minutes = Math.abs(offset % 60);
        const sign = offset < 0 ? '+' : '-';
        
        return `(GMT${sign}${hours}${minutes ? `:${minutes}` : ''})`;
    };

    const formatDateTime = (dateString: string | null) => {
        if (!dateString) return '-';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '-';
            return format(date, 'MMM dd, yyyy, h:mm a');
        } catch {
            return '-';
        }
    };

    const formatDuration = (durationMs: number) => {
        if (!durationMs || durationMs <= 0) return '-';
        
        // Show exact duration in seconds with decimal precision
        const seconds = durationMs / 1000;
        return `${seconds.toFixed(2)}s`;
    };

    const handleRowClick = (run: RunResult) => {
        // Build the URL dynamically using window.location.origin for multi-tenant support
        const baseUrl = window.location.origin;
        const runUrl = `${baseUrl}/dashboard/workflows/runs/${run.runId}`;
        window.open(runUrl, '_blank');
    };

    return isLoading ? (
        <RunSearchTableSkeleton />
    ) : (
        <div className="rounded-md border shadow-sm">
            <Table>
                <TableHeader>
                    <TableRow className="bg-muted/50">
                        <TableHead className="whitespace-nowrap">Flow Name</TableHead>
                        <TableHead className="whitespace-nowrap">Run ID</TableHead>
                        <TableHead className="whitespace-nowrap">Status</TableHead>
                        <TableHead className="whitespace-nowrap">
                            <SortableHeader field="start_time">
                                Start Time {getTimeZoneString()}
                            </SortableHeader>
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                            <SortableHeader field="duration_ms">
                                Duration
                            </SortableHeader>
                        </TableHead>
                        <TableHead className="whitespace-nowrap text-center">Steps</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {runs.length > 0 ? (
                        runs.map((run) => (
                            <TableRow 
                                key={run.runId}
                                className="group hover:bg-muted/50 cursor-pointer transition-colors"
                                onClick={() => handleRowClick(run)}
                            >
                                <TableCell>
                                    <div className="flex flex-col gap-0.5">
                                        <TooltipProvider delayDuration={200}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <span className="font-medium text-sm truncate max-w-[200px] block">
                                                        {run.flowName || 'Unnamed Flow'}
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>{run.flowName || 'Unnamed Flow'}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                        <span onClick={(e) => e.stopPropagation()} className="inline-flex self-start">
                                            <FadeId
                                                id={run.flowId}
                                                idWidth="120px"
                                                height="h-5"
                                                fontSize="text-[10px]"
                                                iconSize="sm"
                                                label="Flow ID"
                                                tooltipSide="bottom"
                                            />
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-1">
                                        <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                                            <FadeId
                                                id={run.runId}
                                                idWidth="140px"
                                                height="h-7"
                                                fontSize="text-xs"
                                                iconSize="sm"
                                                label="Run ID"
                                                tooltipSide="top"
                                            />
                                        </span>
                                        <TooltipProvider delayDuration={200}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRowClick(run);
                                                        }}
                                                        className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-muted"
                                                    >
                                                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                                                    </button>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    Open run details
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    {getStatusBadge(run.status, run.hasFailedSteps)}
                                </TableCell>
                                <TableCell>
                                    <div className="flex flex-col">
                                        <span className="text-sm whitespace-nowrap">
                                            {formatDateTime(run.startTime)}
                                        </span>
                                        {run.finishTime && (
                                            <span className="text-xs text-muted-foreground">
                                                Finished: {formatDateTime(run.finishTime)}
                                            </span>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <span className="font-medium text-sm">
                                        {formatDuration(run.durationMs)}
                                    </span>
                                </TableCell>
                                <TableCell className="text-center">
                                    <Badge variant="secondary" className="font-mono">
                                        {run.stepCount}
                                    </Badge>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={6} className="h-[300px] text-center">
                                <div className="flex flex-col items-center justify-center gap-3">
                                    <Search className="h-12 w-12 text-muted-foreground/50" />
                                    <div className="space-y-1">
                                        <h3 className="font-semibold text-lg">No results found</h3>
                                        <p className="text-muted-foreground text-sm">
                                            No runs match your current filters. Try adjusting your search criteria.
                                        </p>
                                    </div>
                                    <p className="text-xs text-muted-foreground/70 mt-2">
                                        Just ran a flow? New runs may take up to a minute to appear.
                                    </p>
                                </div>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
};
